// Offline submission queue.
//
// Items are persisted in IndexedDB (see offlineDb.js) so large payloads —
// clock-outs carry notes, question answers and a base64 signature — survive
// tab closes without blowing the localStorage quota. An in-memory mirror
// keeps the public read API synchronous for existing consumers (App.vue).
//
// Guarantees relevant to clock events:
// - FIFO replay with per-shift ordering: an item is never sent while an
//   earlier item for the same `shiftKey` is still unsent (a clock-out must
//   not reach the server before its clock-in).
// - Each item carries a stable `clientEventId`; the API dedupes on it, so a
//   retried upload can never double-apply (see OFFLINE_CLOCK_SYNC.md).
// - When a queued clock-in for a potential/recurring shift succeeds, the
//   server returns the real shift id; queued follow-ups for the same
//   shiftKey are rewritten to use it.
// - Network failures (no HTTP response) abort the pass and leave items
//   pending. HTTP failures count as attempts; after MAX_ATTEMPTS the item is
//   marked "failed" (needs attention) and no longer blocks other shifts,
//   but still blocks later items of its own shift.

import { dbGetAll, dbPut, dbDelete, dbClear } from "./offlineDb";

const LEGACY_STORAGE_KEY = "oppo_offline_queue";
const MAX_ATTEMPTS = 5;
const RETRY_INTERVAL_MS = 60 * 1000;

let queue = [];
let listeners = [];
let syncListeners = [];
let initPromise = null;
let processing = false;
let retryTimer = null;
let idbOk = true;
let seqCounter = 0;
let registeredServices = null;

function nextId() {
  seqCounter += 1;
  return `${Date.now()}-${seqCounter}-${Math.random().toString(36).slice(2, 9)}`;
}

export function generateEventId() {
  if (window.crypto && window.crypto.randomUUID) {
    return window.crypto.randomUUID();
  }
  return `${Date.now()}-${Math.random().toString(36).slice(2, 12)}-${Math.random().toString(36).slice(2, 12)}`;
}

function persistItem(item) {
  if (!idbOk) return saveLegacy();
  return dbPut("queue", JSON.parse(JSON.stringify(item))).catch(() => {
    idbOk = false;
    saveLegacy();
  });
}

function unpersistItem(id) {
  if (!idbOk) return saveLegacy();
  return dbDelete("queue", id).catch(() => {
    idbOk = false;
    saveLegacy();
  });
}

// localStorage fallback used only when IndexedDB is unavailable.
function saveLegacy() {
  try {
    localStorage.setItem(LEGACY_STORAGE_KEY, JSON.stringify(queue));
  } catch {
    /* quota — nothing else we can do */
  }
}

function notifyListeners() {
  const pending = getPending();
  listeners.forEach((fn) => fn(pending));
}

function notifySync(result) {
  syncListeners.forEach((fn) => fn(result));
}

/**
 * Load persisted items into memory (IndexedDB, migrating any legacy
 * localStorage queue). Safe to call repeatedly.
 */
export function initQueue() {
  if (initPromise) return initPromise;
  initPromise = (async () => {
    let legacyItems = [];
    try {
      const raw = localStorage.getItem(LEGACY_STORAGE_KEY);
      if (raw) legacyItems = JSON.parse(raw) || [];
    } catch {
      legacyItems = [];
    }

    let stored = [];
    try {
      stored = (await dbGetAll("queue")) || [];
    } catch {
      idbOk = false;
    }

    const known = new Set([...stored, ...queue].map((i) => i.id));
    const migrated = legacyItems.filter((i) => !known.has(i.id));

    // Items enqueued before init finished are already in memory — merge.
    const inMemory = queue;
    const inMemoryIds = new Set(inMemory.map((i) => i.id));
    queue = [...stored.filter((i) => !inMemoryIds.has(i.id)), ...migrated, ...inMemory].sort(
      (a, b) => (a.timestamp < b.timestamp ? -1 : 1),
    );

    if (idbOk) {
      for (const item of migrated) {
        await persistItem(item);
      }
      if (legacyItems.length) localStorage.removeItem(LEGACY_STORAGE_KEY);
    }
    notifyListeners();
  })();
  return initPromise;
}

/**
 * Add an item to the offline queue.
 * @param {string} type - e.g. 'complete-shift-notes', 'clock-in', 'clock-out'
 * @param {object} payload - The data to send when back online
 * @param {object} [options]
 * @param {string} [options.shiftKey] - Groups items that must replay in order
 *   (use the shift id the events were recorded against, temp ids included).
 * @param {string} [options.label] - Human-readable description for the sync
 *   status UI, e.g. "Clock-out — Jane D., 7/20 3:12 PM".
 * @returns {object} the queued item
 */
export function enqueue(type, payload, options = {}) {
  const item = {
    id: nextId(),
    type,
    payload,
    shiftKey: options.shiftKey || null,
    label: options.label || null,
    timestamp: new Date().toISOString(),
    status: "pending",
    attempts: 0,
    lastError: null,
  };
  queue.push(item);
  persistItem(item);
  notifyListeners();
  return item;
}

/** All pending (not yet sent, not permanently failed) items. */
export function getPending() {
  return queue.filter((item) => item.status === "pending");
}

/** Items that exhausted their retries and need user/admin attention. */
export function getFailed() {
  return queue.filter((item) => item.status === "failed");
}

/** Everything still on the device, oldest first. */
export function getAll() {
  return [...queue];
}

/** Pending/failed clock events for a given shift key (for optimistic UI). */
export function getClockEventsForShift(shiftKey) {
  return queue.filter(
    (item) =>
      String(item.shiftKey) === String(shiftKey) &&
      (item.type === "clock-in" || item.type === "clock-out"),
  );
}

function isNetworkError(err) {
  // Only genuine connectivity failures abort the pass. A non-axios throw
  // (handler bug) must fall through to the attempt counter instead, or it
  // would stall the queue forever as "still offline".
  if (!navigator.onLine) return true;
  return !!(err && err.isAxiosError && !err.response);
}

/**
 * Process pending items in FIFO order with per-shift ordering.
 * @param {object} services - Map of type -> async handler(payload) → response
 * @returns {object} { succeeded: number, failed: number, aborted: boolean }
 */
export async function processQueue(services) {
  if (processing) return { succeeded: 0, failed: 0, aborted: false };
  processing = true;
  let succeeded = 0;
  let failed = 0;
  let aborted = false;

  try {
    await initQueue();

    // A shift key becomes blocked when an item of that shift could not be
    // sent this pass — later events for the same shift must wait for it.
    const blockedShiftKeys = new Set();
    queue
      .filter((i) => i.status === "failed" && i.shiftKey)
      .forEach((i) => blockedShiftKeys.add(String(i.shiftKey)));

    for (const item of [...queue]) {
      if (item.status !== "pending") continue;
      if (item.shiftKey && blockedShiftKeys.has(String(item.shiftKey))) continue;

      const handler = services[item.type];
      if (!handler) continue;

      try {
        const result = await handler(item.payload);
        // A clock-in against a potential/recurring template creates the real
        // shift; point every queued follow-up at the id it got.
        if (
          item.type === "clock-in" &&
          result &&
          result.shift &&
          result.shift.id &&
          item.shiftKey
        ) {
          queue
            .filter(
              (other) =>
                other.id !== item.id &&
                String(other.shiftKey) === String(item.shiftKey) &&
                other.payload &&
                other.payload.shiftId !== undefined,
            )
            .forEach((other) => {
              other.payload.shiftId = result.shift.id;
              persistItem(other);
            });
        }
        item.status = "completed";
        succeeded++;
        unpersistItem(item.id);
      } catch (err) {
        if (isNetworkError(err)) {
          // Still offline (or the connection dropped mid-pass): stop, leave
          // everything pending, don't burn attempts.
          aborted = true;
          break;
        }
        if (err && err.response && err.response.status === 409 && err.response.data?.inFlight) {
          // Server is already processing a duplicate — leave pending, retry later.
          if (item.shiftKey) blockedShiftKeys.add(String(item.shiftKey));
          continue;
        }
        item.attempts = (item.attempts || 0) + 1;
        item.lastError =
          err?.response?.data?.error || err?.message || "Upload failed";
        if (item.attempts >= MAX_ATTEMPTS) {
          item.status = "failed";
          failed++;
        }
        if (item.shiftKey) blockedShiftKeys.add(String(item.shiftKey));
        persistItem(item);
      }
    }

    queue = queue.filter((item) => item.status !== "completed");
    notifyListeners();
  } finally {
    processing = false;
  }

  const result = { succeeded, failed, aborted };
  if (succeeded > 0 || failed > 0) notifySync(result);
  return result;
}

/** Move permanently-failed items back to pending (manual retry). */
export function retryFailed() {
  getFailed().forEach((item) => {
    item.status = "pending";
    item.attempts = 0;
    item.lastError = null;
    persistItem(item);
  });
  notifyListeners();
}

/**
 * Try to drain the queue right now with the handlers registered at startup
 * (manual "Sync now" / "Retry" buttons).
 */
export function flushNow() {
  if (!registeredServices || !isOnline()) {
    return Promise.resolve({ succeeded: 0, failed: 0, aborted: true });
  }
  return processQueue(registeredServices);
}

/** Discard a queued item (confirm with the user first — this loses data). */
export function removeItem(id) {
  queue = queue.filter((item) => item.id !== id);
  unpersistItem(id);
  notifyListeners();
}

/** Wipe the queue (logout of a different user, etc.). */
export function clearQueue() {
  queue = [];
  listeners.length && notifyListeners();
  return dbClear("queue").catch(() => {
    /* ignore */
  });
}

/**
 * Subscribe to queue changes.
 * @param {function} fn - Called with pending items array on changes
 * @returns {function} Unsubscribe function
 */
export function onChange(fn) {
  listeners.push(fn);
  return () => {
    listeners = listeners.filter((l) => l !== fn);
  };
}

/**
 * Subscribe to sync results ({succeeded, failed, aborted}) after each
 * processed pass that did work.
 */
export function onSyncResult(fn) {
  syncListeners.push(fn);
  return () => {
    syncListeners = syncListeners.filter((l) => l !== fn);
  };
}

/**
 * Check if browser is online.
 */
export function isOnline() {
  return navigator.onLine;
}

/**
 * Initialize online/offline event listeners and background retry.
 * Call this once at app startup.
 * @param {object} options - { onOnline, onOffline, services, onQueueProcessed }
 * @returns {function} Cleanup function
 */
export function initConnectivityListeners(options = {}) {
  const { onOnline, onOffline, services } = options;
  registeredServices = services || null;

  const flush = async () => {
    if (!services || !isOnline()) return;
    if (getPending().length === 0) return;
    const result = await processQueue(services);
    if ((result.succeeded > 0 || result.failed > 0) && options.onQueueProcessed) {
      options.onQueueProcessed(result);
    }
  };

  const handleOnline = async () => {
    if (onOnline) onOnline();
    await flush();
  };

  const handleOffline = () => {
    if (onOffline) onOffline();
  };

  window.addEventListener("online", handleOnline);
  window.addEventListener("offline", handleOffline);

  // Flush anything left over from a previous session once loaded, and keep a
  // slow retry loop for flaky connections where the 'online' event never fires.
  initQueue().then(flush);
  retryTimer = setInterval(flush, RETRY_INTERVAL_MS);

  return () => {
    window.removeEventListener("online", handleOnline);
    window.removeEventListener("offline", handleOffline);
    if (retryTimer) clearInterval(retryTimer);
  };
}
